import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { sassLine } from '../sass/engine.js'

/**
 * Hold-to-talk speech recognition, where the platform actually has it.
 *
 * Chrome on Android recognises Lithuanian through Google's server-side engine.
 * Apple does not: WebKit's recognition rides on Apple's dictation stack, which
 * has no Lithuanian in it at all, and inside an installed web app it returns
 * nothing in any language. `supported` reflects that per language, so the caller
 * can hand the job to `useRecorder` rather than offer a microphone button that
 * would quietly do nothing.
 *
 * This used to report `supported: true` in an iPhone Safari tab, where the
 * constructor exists and lt-LT then fails silently — the honest answer depends
 * on the language being asked for, which is why it is recomputed when that
 * changes rather than decided once at mount.
 *
 * The transcript never leaves this hook by design. Watching words appear and
 * correct themselves mid-sentence is the worst part of dictation — it invites
 * you to proofread a machine's first draft instead of just saying what you ate.
 * The caller gets one finished string, once, and shows a processing state until
 * then.
 */

type SpeechRecognitionLike = {
  lang: string
  continuous: boolean
  interimResults: boolean
  maxAlternatives: number
  start: () => void
  stop: () => void
  abort: () => void
  onresult: ((event: SpeechRecognitionEventLike) => void) | null
  onerror: ((event: { error: string }) => void) | null
  onend: (() => void) | null
  onspeechend: (() => void) | null
}

interface SpeechAlternative {
  transcript: string
  confidence?: number
}

interface SpeechRecognitionEventLike {
  resultIndex: number
  results: ArrayLike<ArrayLike<SpeechAlternative> & { isFinal: boolean; length: number }>
}

type SpeechRecognitionCtor = new () => SpeechRecognitionLike

function getConstructor(): SpeechRecognitionCtor | null {
  const w = window as unknown as {
    SpeechRecognition?: SpeechRecognitionCtor
    webkitSpeechRecognition?: SpeechRecognitionCtor
  }
  return w.SpeechRecognition ?? w.webkitSpeechRecognition ?? null
}

/** Including the iPad, which reports a Mac user agent and has a touchscreen. */
function isApple(): boolean {
  return (
    /iPad|iPhone|iPod/.test(navigator.userAgent) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
  )
}

/** Installed web apps report the API and then produce no results at all. */
function isStandalone(): boolean {
  return (
    (navigator as unknown as { standalone?: boolean }).standalone === true ||
    window.matchMedia?.('(display-mode: standalone)').matches === true ||
    window.matchMedia?.('(display-mode: fullscreen)').matches === true
  )
}

/**
 * Whether this device can turn speech into words itself, for this language.
 *
 * Two separate refusals, and the language is half of the question. Apple has no
 * Lithuanian to offer in any context; on top of that, an installed Apple web app
 * hears nothing whatever it is asked for.
 */
export function onDeviceSpeech(lang: 'lt' | 'en'): boolean {
  if (getConstructor() == null) return false
  if (!isApple()) return true
  return lang === 'en' && !isStandalone()
}

/**
 * Pick the best reading of each phrase.
 *
 * The engine returns several candidates per utterance and the first is not
 * always the most confident one — asking for alternatives and choosing on
 * confidence measurably improves Lithuanian food words, which sit outside the
 * general language model ("varškė" competes with "varškės", "varško").
 */
function bestTranscript(event: SpeechRecognitionEventLike): string {
  let text = ''
  for (let i = 0; i < event.results.length; i++) {
    const result = event.results[i]
    if (!result?.isFinal) continue
    let best: SpeechAlternative | null = null
    for (let a = 0; a < result.length; a++) {
      const alternative = result[a]
      if (!alternative) continue
      if (best == null || (alternative.confidence ?? 0) > (best.confidence ?? 0)) {
        best = alternative
      }
    }
    if (best) text += `${best.transcript} `
  }
  return text.replace(/\s+/g, ' ').trim()
}

export interface SpeechState {
  supported: boolean
  listening: boolean
  error: string | null
}

export function useSpeech(lang: 'lt' | 'en', onFinal: (transcript: string) => void) {
  const recognition = useRef<SpeechRecognitionLike | null>(null)
  const transcript = useRef('')
  // The callback changes on every render of the caller; holding it in a ref
  // keeps `start` stable so a re-render mid-utterance cannot restart the engine.
  const finalHandler = useRef(onFinal)
  finalHandler.current = onFinal

  const supported = useMemo(() => onDeviceSpeech(lang), [lang])
  const [state, setState] = useState<Omit<SpeechState, 'supported'>>(() => ({
    listening: false,
    error: null,
  }))

  const stop = useCallback(() => {
    // `stop` asks for the final result and fires onend; `abort` would throw the
    // utterance away, which is the opposite of what releasing the button means.
    recognition.current?.stop()
    setState((s) => ({ ...s, listening: false }))
  }, [])

  /** Throw the utterance away — for a sheet dismissed rather than finished. */
  const cancel = useCallback(() => {
    const instance = recognition.current
    recognition.current = null
    transcript.current = ''
    if (instance) {
      instance.onresult = null
      instance.onend = null
      instance.onerror = null
      instance.abort()
    }
    setState((s) => (s.listening || s.error != null ? { listening: false, error: null } : s))
  }, [])

  const start = useCallback(() => {
    const Ctor = getConstructor()
    if (!Ctor) return
    recognition.current?.abort()
    transcript.current = ''

    const instance = new Ctor()
    instance.lang = lang === 'lt' ? 'lt-LT' : 'en-US'
    // Continuous, because "dvi riekės duonos ir šaukštas medaus" has a pause in
    // the middle and a single-shot recogniser stops at the first one, throwing
    // away the half of the sentence that had not been said yet.
    instance.continuous = true
    instance.interimResults = false
    instance.maxAlternatives = 3

    instance.onresult = (event) => {
      const text = bestTranscript(event)
      if (text) transcript.current = `${transcript.current} ${text}`.trim()
    }
    instance.onerror = (event) => {
      setState((s) => ({
        ...s,
        listening: false,
        error:
          event.error === 'not-allowed'
            ? (sassLine('error_mic_denied', {}) ?? 'Mikrofono prieiga atmesta')
            : event.error === 'no-speech'
              ? (sassLine('error_mic', {}) ?? 'Nieko negirdėti')
              : event.error === 'aborted'
                ? null
                : (sassLine('error_mic', {}) ?? 'Atpažinimas nepavyko'),
      }))
    }
    instance.onend = () => {
      setState((s) => ({ ...s, listening: false }))
      const text = transcript.current.trim()
      transcript.current = ''
      if (text) finalHandler.current(text)
    }

    recognition.current = instance
    setState((s) => ({ ...s, listening: true, error: null }))
    try {
      instance.start()
    } catch {
      // Already running: a double-tap on the hold button, which is harmless.
    }
  }, [lang])

  useEffect(() => () => recognition.current?.abort(), [])

  return { ...state, supported, start, stop, cancel }
}
